import { Badge, Button, Modal } from '@evaneos/b2b-ui/legacy';
import cx from 'classnames';
import { ChangeEvent, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Dropdown } from '../../../design-system/Dropdown/ControlledDropdown';
import { AgentIsAway } from '../../../design-system/Icons/AgentIsAway';
import { ArrowDropDown } from '../../../design-system/Icons/ArrowDropDown';
import { ArrowDropUp } from '../../../design-system/Icons/ArrowDropUp';
import { Help } from '../../../design-system/Icons/Help';
import { Logout } from '../../../design-system/Icons/Logout';
import { MoreVert as MoreVertIcon } from '../../../design-system/Icons/MoreVert';
import { Translate } from '../../../design-system/Icons/Translate';
import { WorkloadIcon } from '../../../design-system/Icons/Workload';
import { MenuWithTransition as Menu } from '../../../design-system/Menu';
import { Avatar } from '../../../resources/agent/components/Avatar';
import {
    useGetAgentAwayStatus,
    useGetAgentWorkload,
} from '../../../resources/agentSettings/queries';
import { useGetCollectCapacityReminder } from '../../../resources/agentSettings/hooks';
import { AgencyCapacityReminderModal } from '../../../resources/agentSettings/components/AgencyCapacityReminderModal';
import { AgentOverloadedForTwoDaysModal } from '../../../resources/agentSettings/components/AgentOverloadedForTwoDaysModal';
import { AgentOverloadedReasonModal } from '../../../resources/agentSettings/components/AgentOverloadedReasonModal';
import { AgentWorkloadForm } from '../../../resources/agentSettings/components/AgentWorkloadForm';
import { NoAgentWorkloadFeedbackForTwoWeeksModal } from '../../../resources/agentSettings/components/NoAgentWorkloadFeedbackForTwoWeeksModal';
import { WorkloadStatusComponent } from '../../../resources/agentSettings/components/Workload';
import { WorkloadStatus } from '../../../resources/agentSettings/types';
import {
    AgencyCapacityReminder,
    AgentWorkloadReminder,
} from '../../../resources/agentSettings/domain/collectCapacityReminder/CollectCapacityReminderConfigurator';
import { logoutAndNavigateToLogin } from '../../../resources/login/operations';
import { useTrackEventAmplitude } from '../../analytics';
import { useLocale } from '../../providers/LocalizationProvider';
import { availableLocales } from '../../intl/operations';
import { AllTranslationKeys, AvailableLocale } from '../../intl/types';
import { useAuth } from '../../providers/AuthProvider';
import { useGetLoggedAgent } from '../../../resources/agent/hooks';

import { useIsCalendlyEnabled } from '../../../resources/calendly/AB_calendly-hooks';

import { AB_CalendlyNavbarButton } from './AB_CalendlyNavbarButton';
import { AgentAwayStatusModal } from './AgentAwayStatusModal';
import s from './Navbar.css';
import { Searchbar } from './Searchbar';

const SHARE_FEEDBACK_URL =
    'https://travelsuite.evaneos.com/agency-manager/community?ms_url=%2Fdiscussions%2Fforums%2F8aace9d4-991e-48f6-ae8c-5b5ff3dcf7f2';

export function LayoutNavbar(): JSX.Element {
    const { locale, setLocale } = useLocale();
    const intl = useIntl();
    const { trackEvent } = useTrackEventAmplitude();

    const agent = useGetLoggedAgent();
    const { isAuthenticated } = useAuth();

    const { data: agentAwayStatus } = useGetAgentAwayStatus();
    const { data: agentWorkload } = useGetAgentWorkload();
    const collectCapacityReminder = useGetCollectCapacityReminder();

    // BEGIN - AB ab_calendly - PR #TODO_AFTER_PR_CREATION_ab_calendly
    const isCalendlyEnabled = useIsCalendlyEnabled();
    // END - AB ab_calendly - PR #TODO_AFTER_PR_CREATION_ab_calendly

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
    const [desiredLanguage, setDesiredLanguage] = useState(locale);

    const inbentaLocaleToRenderLink =
        locale === 'fr' ? '/knowledge-base?locale=fr' : '/knowledge-base?locale=en';

    const [isAgentAwayModalOpen, setIsAgentAwayModalOpen] = useState(false);
    const [switchToggledOn, setSwitchToggledOn] = useState<boolean>(false);

    const [isAgentOverloadedReasonModalOpen, setIsAgentOverloadedReasonModalOpen] = useState(false);

    function toggleMenu(): void {
        setIsMenuOpen(!isMenuOpen);
    }

    function logout(): void {
        logoutAndNavigateToLogin();
    }

    function displayIsAgentAwayModal(): void {
        setIsAgentAwayModalOpen(true);
        setSwitchToggledOn(agentAwayStatus === true);
    }

    function displayChangeLanguageModal(): void {
        setIsLanguageModalOpen(true);
    }

    function hideChangeLanguageModal(): void {
        setIsLanguageModalOpen(false);
    }

    function changeLanguage(event: ChangeEvent<HTMLInputElement>): void {
        const desiredLanguage = event.currentTarget.value as AvailableLocale;
        setDesiredLanguage(desiredLanguage);
    }

    function submitChangeLanguage(): void {
        setLocale(desiredLanguage);
        hideChangeLanguageModal();
    }

    function renderUserInfo({ isOpened }: { isOpened: boolean }): JSX.Element {
        return (
            <div data-testid="navbar-userinfo">
                <div className={s.username}>
                    {agent && (
                        <div className={s.agent} data-testid="navbar-agent-avatar">
                            <WorkloadStatusComponent
                                workloadStatus={agentWorkload?.status ?? WorkloadStatus.Good}
                            />
                            <Avatar name={agent.name} photoUrl={agent.photoUrl} />
                        </div>
                    )}
                    {isOpened && <ArrowDropUp color="var(--white)" className={s.openedIcon} />}
                    {!isOpened && <ArrowDropDown color="var(--white)" />}
                </div>
                <div className={s.mobileIcon}>
                    <MoreVertIcon color="var(--white)" />
                </div>
            </div>
        );
    }

    return (
        <nav className={s.navbarContainer}>
            <div className={s.iconTitle}>
                <div className={s.menuLeft}>
                    {isAuthenticated && (
                        <button
                            type="button"
                            onClick={toggleMenu}
                            data-testid="menu-button"
                            className={s.burger}
                            aria-label={intl.formatMessage({ id: 'global.menu' })}
                        >
                            <span />
                        </button>
                    )}
                    <div className={s.titleContainer}>
                        <h3 className={s.title}>Request Manager</h3>
                    </div>
                    {isAuthenticated && <Searchbar />}
                </div>
                <div className={s.menuRight}>
                    {/* BEGIN - AB ab_calendly - PR #TODO_AFTER_PR_CREATION_ab_calendly */}
                    {isCalendlyEnabled && <AB_CalendlyNavbarButton />}
                    {/* END - AB ab_calendly - PR #TODO_AFTER_PR_CREATION_ab_calendly */}
                    <a
                        className={cx(s.navbarLink, s.desktopOnlyDisplay)}
                        href={SHARE_FEEDBACK_URL}
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        <span>
                            {intl.formatMessage({
                                id: 'navbar.share_idea',
                            })}
                        </span>
                    </a>
                    <a
                        className={cx(s.navbarLink, s.desktopOnlyDisplay)}
                        href={inbentaLocaleToRenderLink}
                        target="_blank"
                        rel="noreferrer noopener"
                        onClick={() => {
                            trackEvent((ampli, defaultProperties) => {
                                ampli.faqClicked({ ...defaultProperties, click_origin: 'navbar' });
                            });
                        }}
                    >
                        <Help color="var(--white)" />
                        <span>{intl.formatMessage({ id: 'navbar.help_center' })}</span>
                    </a>
                    {agentAwayStatus === true && (
                        <Badge
                            className={s.agentAwayBadge}
                            variant={'warning'}
                            onClick={displayIsAgentAwayModal}
                            data-testid="badge-agent-away"
                        >
                            <div className={s.awayStatusEllipse} />
                            {intl.formatMessage({
                                id: 'agent_settings.is_away.badge_status',
                            })}
                        </Badge>
                    )}

                    {/* Mobile navbar */}
                    <Dropdown
                        button={() => (
                            <div>
                                <Help color="var(--white)" />
                            </div>
                        )}
                        buttonClass={cx(s.navbarDropdownButton, s.navbarLink, s.mobileOnlyDisplay)}
                        className={s.dropdown}
                    >
                        <button
                            type="button"
                            onClick={() =>
                                window.open(
                                    inbentaLocaleToRenderLink,
                                    '_blank',
                                    'noopener,noreferrer',
                                )
                            }
                            className={cx(s.dropdownItem, s.borderNone)}
                        >
                            <p className="annotation">
                                {intl.formatMessage({
                                    id: 'navbar.help_center',
                                })}
                            </p>
                        </button>
                        <button
                            type="button"
                            onClick={() =>
                                window.open(SHARE_FEEDBACK_URL, '_blank', 'noopener,noreferrer')
                            }
                            className={cx(s.dropdownItem, s.borderNone)}
                        >
                            <p className="annotation">
                                {intl.formatMessage({
                                    id: 'navbar.share_idea',
                                })}
                            </p>
                        </button>
                    </Dropdown>
                    {agent && (
                        <Dropdown
                            button={renderUserInfo}
                            buttonClass={s.navbarDropdownButton}
                            className={s.dropdown}
                            menuItemClassName={s.borderNone}
                        >
                            <button
                                type="button"
                                disabled
                                className={cx(s.dropdownItem, s.dropdownUsername, 'title-s')}
                            >
                                <Avatar
                                    name={agent.name}
                                    photoUrl={agent.photoUrl}
                                    className={s.avatarDropdownOption}
                                />

                                {agent.name}
                            </button>
                            <div
                                className={s.dropdownItem}
                                data-testid="button-fill-workload-status"
                            >
                                <WorkloadIcon color="var(--ink)" />
                                <div className={s.dropdownItemWrapper}>
                                    <p>
                                        <FormattedMessage id="agent_settings.workload_status.current.title" />
                                    </p>
                                    <p
                                        style={{
                                            fontSize: `1.2rem`,
                                            lineHeight: `1.6rem`,
                                            color: `var(--ink-light)`,
                                            marginBottom: `10px`,
                                        }}
                                    >
                                        <FormattedMessage id="agent_settings.workload_status.current.subtitle" />
                                    </p>
                                    <AgentWorkloadForm
                                        askForReasonIfOverloaded={false}
                                        onOverloadedStatusSelected={() =>
                                            setIsAgentOverloadedReasonModalOpen(true)
                                        }
                                    />
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={displayIsAgentAwayModal}
                                className={s.dropdownItem}
                                data-testid="button-open-modal-agent-away"
                            >
                                <AgentIsAway color="var(--ink)" />
                                <div className={s.dropdownItemWrapper}>
                                    <p>
                                        <FormattedMessage id="agent_settings.is_away.modal.title" />
                                    </p>
                                    <p className={cx(s.isAgentAwayCaption, 'caption')}>
                                        <FormattedMessage id="agent_settings.is_away.dropdown" />
                                    </p>
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={displayChangeLanguageModal}
                                className={s.dropdownItem}
                                data-testid="button-changelanguage"
                            >
                                <Translate color="var(--ink)" />
                                <p>
                                    <FormattedMessage id="languages.title" />
                                </p>
                            </button>
                            <button
                                type="button"
                                onClick={logout}
                                className={cx(s.borderNone, s.dropdownItem)}
                                data-testid="button-logout"
                            >
                                <Logout color="var(--ink)" />
                                <p>
                                    <FormattedMessage id="global.logout" />
                                </p>
                            </button>
                        </Dropdown>
                    )}
                </div>
            </div>
            <Modal
                isOpen={isLanguageModalOpen}
                onRequestClose={hideChangeLanguageModal}
                className={s.modalContent}
                title={intl.formatMessage({
                    id: 'languages.title',
                })}
                mainButton={
                    <Button
                        priority="primary"
                        onClick={submitChangeLanguage}
                        data-testid="submit-modal-language"
                    >
                        <FormattedMessage id="form.submit" />
                    </Button>
                }
                secondaryButton={
                    <Button
                        priority="secondary"
                        onClick={hideChangeLanguageModal}
                        data-testid="cancel-modal-language"
                    >
                        <FormattedMessage id="form.cancel" />
                    </Button>
                }
            >
                <p>
                    <FormattedMessage id="languages.explanation" />
                </p>
                {Object.keys(availableLocales).map((language) => (
                    <p key={language}>
                        <input
                            type="radio"
                            value={language}
                            name="language"
                            id={language}
                            defaultChecked={locale === language}
                            onChange={changeLanguage}
                        />
                        <label htmlFor={language}>
                            <FormattedMessage id={`languages.${language}` as AllTranslationKeys} />
                        </label>
                    </p>
                ))}
            </Modal>

            <AgentAwayStatusModal
                modalOpened={isAgentAwayModalOpen}
                agentAwayStatusChecked={switchToggledOn}
                handleAgentAwayStatus={(checked) => setSwitchToggledOn(checked)}
                openModal={(open) => setIsAgentAwayModalOpen(open)}
            />

            <AgentOverloadedForTwoDaysModal
                reminder={collectCapacityReminder as AgentWorkloadReminder | null}
            />

            <NoAgentWorkloadFeedbackForTwoWeeksModal
                reminder={collectCapacityReminder as AgentWorkloadReminder | null}
            />

            <AgentOverloadedReasonModal
                isOpen={isAgentOverloadedReasonModalOpen}
                onClose={() => setIsAgentOverloadedReasonModalOpen(false)}
            />

            <AgencyCapacityReminderModal
                reminder={collectCapacityReminder as AgencyCapacityReminder | null}
            />

            <Menu
                locale={locale}
                opened={isMenuOpen}
                onClose={toggleMenu}
                username={agent ? agent.name : ''}
            />
            {agentAwayStatus === true && (
                <div className={s.agentAwayBanner} onClick={displayIsAgentAwayModal}>
                    <div className={s.awayStatusEllipse} />
                    <span className="annotation">
                        {intl.formatMessage({
                            id: 'agent_settings.is_away.banner_status',
                        })}
                    </span>
                </div>
            )}
        </nav>
    );
}
